import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { fetchSOWDTO } from '../services/api';
import { isValueEmpty } from '../services/utils';
import { createTicketScopedPersistence } from './ticketScopedPersistence';

// Follows the ticket in the URL, including in-app switches that never reload the page.
const ticketPersistence = createTicketScopedPersistence({
  prefix: 'sow-data-storage-',
  persistedFields: ['sowData', 'lastUpdated'],
  // Includes the request flags: a request still running for the previous ticket must not
  // make the new ticket's fetch bail out at the loading guard.
  resetState: { sowData: null, sowError: null, lastUpdated: null, sowLoading: false, sowLoadingTime: 0 }
});

export const useSOWDataStore = create(
  persist(
    (set, get) => ({
      sowData: null,
      sowLoading: false,
      sowLoadingTime: 0,
      sowError: null,
      lastUpdated: null,

      // Fetch data from backend
      fetchSOWData: async () => {
        const pendingSwitch = ticketPersistence.followTicket(useSOWDataStore);
        if (pendingSwitch) await pendingSwitch;

        // only allow 5 seconds for the loading to complete
        if (get().sowLoadingTime + 5000 <= Date.now()) {
          set({ sowLoading: false, sowLoadingTime: 0 });
        }
        
        if (get().sowLoading) {
          return get().sowData;
        }
        
        set({ sowLoading: true, sowError: null, sowLoadingTime: Date.now()});
        const isStillCurrentTicket = ticketPersistence.trackTicket();

        try {
          const data = await fetchSOWDTO();
          if (!isStillCurrentTicket()) {
            // The technician switched tickets while this loaded; it is not theirs to show or save.
            set({ sowLoading: false, sowLoadingTime: 0 });
            return data;
          }
          if (data === undefined) {
            // Skipped because an identical request is already running; keep what we have.
            set({ sowLoading: false, sowLoadingTime: 0 });
            return get().sowData;
          }
          set({
            sowData: data,
            sowLoading: false,
            sowError: null,
            sowLoadingTime: 0,
            lastUpdated: new Date().toISOString()
          });
          return data;
        } catch (error) {
          set(isStillCurrentTicket()
            ? { sowError: error.message, sowLoading: false, sowLoadingTime: 0 }
            : { sowLoading: false, sowLoadingTime: 0 });
          throw error;
        }
      },

      // Get current SOW data
      getSOWData: () => {
        return get().sowData;
      },
      getSOWName: () => {
        let dt = get().sowData
        if (isValueEmpty(dt)) {
          return '';
        } else {
          return dt.workorderKey;
        }
      },
      
      // Update specific field in SOW data
      updateField: (fieldPath, value) => {
        const { sowData } = get();
        
        if (!sowData) {
          throw new Error('No SOW data available to update');
        }

        const path = fieldPath.split('.');
        const newData = { ...sowData };
        let current = newData;

        for (let i = 0; i < path.length - 1; i++) {
          if (!current[path[i]]) {
            current[path[i]] = {};
          }
          current = current[path[i]];
        }

        current[path[path.length - 1]] = value;

        set({ 
          sowData: newData,
          lastUpdated: new Date().toISOString()
        });

        return newData;
      },

      // Update multiple fields at once
      updateFields: (updates) => {
        const { sowData } = get();
        
        if (!sowData) {
          throw new Error('No SOW data available to update');
        }

        const newData = { ...sowData };
        
        Object.entries(updates).forEach(([fieldPath, value]) => {
          const path = fieldPath.split('.');
          let current = newData;

          for (let i = 0; i < path.length - 1; i++) {
            if (!current[path[i]]) {
              current[path[i]] = {};
            }
            current = current[path[i]];
          }

          current[path[path.length - 1]] = value;
        });

        set({ 
          sowData: newData,
          lastUpdated: new Date().toISOString()
        });

        return newData;
      },

      // Replace entire SOW data object
      setSOWData: (newData) => {
        set({ 
          sowData: newData,
          lastUpdated: new Date().toISOString()
        });
      },

      // Clear all SOW data
      clearSOWData: () => {
        set({ 
          sowData: null,
          sowError: null,
          lastUpdated: null 
        });
      }
    }),
    // Partitioned per ticket (issueKey+token from the URL) so this tab's "current SOW"
    // cache can never bleed into another ticket's tab, even across in-app switches.
    ticketPersistence.persistOptions
  )
);